import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState, lazy } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import publicApi, { getRankingEntries, getRankingReplacements } from '../../api/publicApi';
import { formatDate } from '../../utils/labels';
import { CONTINENTS, getContinent, getCountryName, getNocFromMapName } from '../../utils/nocContinent';
import {
  buildLineCrossersFromSnapshots,
  formatLineCrosserMeta,
  matchesLineCrosserFilters,
} from '../../utils/rankingsAnalytics';
import RankingTable from './RankingTable';
import FilterSelect from './FilterSelect';
import CountryFlag from './CountryFlag';
import PlayerCell from './PlayerCell';
import BubbleWatchRail from './BubbleWatchRail';
import AthleteHubRail from './AthleteHubRail';
import heroGolfImage from '../../assets/hero-golf-paris.jpg';
import './RankingsPage.css';

const PlayerProgressionModal = lazy(() => import('./PlayerProgressionModal'));

const HERO_BACKGROUND = `linear-gradient(100deg, rgba(5, 20, 12, 0.93) 0%, rgba(5, 20, 12, 0.82) 30%, rgba(5, 20, 12, 0.38) 50%, rgba(5, 20, 12, 0.08) 72%), url(${heroGolfImage})`;

const SHOW_EXTENDED_RANKS = String(process.env.REACT_APP_SHOW_EXTENDED_RANKS || '').toLowerCase() === 'true';
const QUALIFICATION_CUTOFF = 60;
const DEFAULT_PAGE_SIZE = SHOW_EXTENDED_RANKS ? 80 : 60;
const MOBILE_WATCH_PREVIEW_COUNT = 3;
const FULL_HISTORY_LIMIT = 10;
const RECENT_HISTORY_LIMIT = 5;
const CUT_LINE_WATCH_MIN = 55;
const CUT_LINE_WATCH_MAX = SHOW_EXTENDED_RANKS ? 65 : QUALIFICATION_CUTOFF;
const REALLOCATION_RESERVE_MIN = 61;
const REALLOCATION_RESERVE_MAX = 70;

const GENDER_OPTIONS = [
  { id: 'men', label: 'Men', tabLabel: 'MEN' },
  { id: 'women', label: 'Women', tabLabel: 'WOMEN' },
];

const CONTINENT_FILTER_OPTIONS = [
  { value: '', label: 'All continents' },
  ...CONTINENTS.map((continent) => ({ value: continent, label: continent })),
];

const listSortDate = (list) =>
  new Date(list.snapshotDate || list.updatedAt || list.createdAt).getTime();

const entryKey = (entry) =>
  `${entry?.firstName || ''} ${entry?.lastName || ''}|${entry?.country || ''}`
    .trim()
    .toLowerCase();

const cutoffDistance = (rank) => Math.abs((rank || QUALIFICATION_CUTOFF) - QUALIFICATION_CUTOFF);

const buildMovementAnalytics = (history = {}, entries = []) => {
  const entryLookup = new Map(entries.map((entry) => [entryKey(entry), entry]));
  const rankMovers = [];
  const moverKeys = new Set();

  Object.entries(history).forEach(([key, rows]) => {
    if (!rows?.length || rows.length < 2) return;
    const previous = rows[rows.length - 2];
    const last = rows[rows.length - 1];
    const entry = entryLookup.get(key) || last.entry;
    if (!entry) return;

    const rankDelta = (previous.olympicRank || 0) - (last.olympicRank || 0);
    const base = {
      key,
      entry,
      history: rows,
      startRank: previous.olympicRank,
      endRank: last.olympicRank,
    };

    if (rankDelta !== 0) {
      rankMovers.push({
        ...base,
        delta: rankDelta,
        absDelta: Math.abs(rankDelta),
      });
      moverKeys.add(key);
      return;
    }
  });

  entries.forEach((entry) => {
    const key = entryKey(entry);
    if (moverKeys.has(key) || !['up', 'down'].includes(entry.changeDirection)) return;

    const delta = entry.changeDirection === 'up' ? 1 : -1;
    rankMovers.push({
      key,
      entry,
      history: history[key] || [{ label: 'Current', olympicRank: entry.olympicRank, entry }],
      startRank: null,
      endRank: entry.olympicRank,
      delta,
      absDelta: 1,
      isDirectionalOnly: true,
    });
  });

  return { rankMovers };
};

const sortByMovement = (a, b) =>
  b.absDelta - a.absDelta || cutoffDistance(a.endRank) - cutoffDistance(b.endRank);

const rowHistory = (entry, history = {}) =>
  history[entryKey(entry)] || [{ label: 'Now', olympicRank: entry.olympicRank, entry }];

const buildTableRankHistory = (currentEntries = [], previousEntries = []) => {
  const previousByKey = new Map(previousEntries.map((entry) => [entryKey(entry), entry]));
  const history = {};

  currentEntries.forEach((entry) => {
    const previous = previousByKey.get(entryKey(entry));
    if (!previous) return;
    history[entryKey(entry)] = [
      { label: 'LW', olympicRank: previous.olympicRank, entry: previous },
      { label: 'Now', olympicRank: entry.olympicRank, entry },
    ];
  });

  return history;
};

const buildCutLineWatch = (entries = [], movementByKey = new Map(), history = {}) =>
  entries
    .filter(
      (entry) =>
        entry.olympicRank >= CUT_LINE_WATCH_MIN &&
        entry.olympicRank <= CUT_LINE_WATCH_MAX
    )
    .map((entry) => {
      const movement = movementByKey.get(entryKey(entry));
      const lineStatus =
        entry.olympicRank === QUALIFICATION_CUTOFF
          ? 'on'
          : entry.olympicRank < QUALIFICATION_CUTOFF
            ? 'inside'
            : 'outside';

      return {
        key: entryKey(entry),
        entry,
        history: rowHistory(entry, history),
        lineStatus,
        delta: movement?.delta || 0,
        absDelta: movement?.absDelta || 0,
        startRank: movement?.startRank || null,
        endRank: entry.olympicRank,
      };
    })
    .sort((a, b) => a.endRank - b.endRank);

const buildReallocationReserve = (entries = [], movementByKey = new Map(), history = {}) =>
  entries
    .filter(
      (entry) =>
        entry.olympicRank >= REALLOCATION_RESERVE_MIN &&
        entry.olympicRank <= REALLOCATION_RESERVE_MAX
    )
    .map((entry) => {
      const movement = movementByKey.get(entryKey(entry));
      return {
        key: entryKey(entry),
        entry,
        history: rowHistory(entry, history),
        delta: movement?.delta || 0,
        absDelta: movement?.absDelta || 0,
        startRank: movement?.startRank || null,
        endRank: entry.olympicRank,
      };
    })
    .sort((a, b) => a.endRank - b.endRank);

const listDateKey = (list) => {
  const date = new Date(list?.snapshotDate || list?.updatedAt || list?.createdAt || '');
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const findMatchingSnapshotList = (lists, targetList) => {
  if (!lists?.length) return null;
  const targetDate = listDateKey(targetList);
  return lists.find((list) => listDateKey(list) === targetDate) || lists[0];
};

const buildWeekOptions = (lists) => {
  if (!lists?.length) return [];
  const sorted = [...lists].sort((a, b) => listSortDate(a) - listSortDate(b));
  return sorted.map((list, i) => ({
    value: list._id,
    label: `Week ${i + 1} (${formatDate(list.snapshotDate || list.updatedAt || list.createdAt)})`,
  }));
};

const buildPageNumbers = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) result.push('ellipsis');
    result.push(p);
  });
  return result;
};

const RankingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [publishedLists, setPublishedLists] = useState({ men: [], women: [] });
  const [listsLoading, setListsLoading] = useState(true);
  const [listsError, setListsError] = useState('');

  const [gender, setGender] = useState('men');
  const [selectedListId, setSelectedListId] = useState(null);
  const [allEntriesByGender, setAllEntriesByGender] = useState({ men: [], women: [] });
  const [previousEntriesByGender, setPreviousEntriesByGender] = useState({ men: [], women: [] });
  const [rankHistoryByGender, setRankHistoryByGender] = useState({ men: {}, women: {} });
  const [replacementGroups, setReplacementGroups] = useState([]);
  const [replacementReportDate, setReplacementReportDate] = useState(null);
  const [replacementDisplayMode, setReplacementDisplayMode] = useState('none');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [page, setPage] = useState(1);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [expandedMobileSections, setExpandedMobileSections] = useState({});
  const [activeMobileView, setActiveMobileView] = useState('rankings');
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState('');

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [continent, setContinent] = useState('');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const handleChange = () => setIsMobileView(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const listsForGender = useMemo(
    () => publishedLists[gender] || [],
    [publishedLists, gender]
  );
  const weekOptions = useMemo(() => buildWeekOptions(listsForGender), [listsForGender]);

  const activeList = useMemo(() => {
    if (!listsForGender.length) return null;
    if (selectedListId) {
      return listsForGender.find((l) => l._id === selectedListId) || listsForGender[0];
    }
    return listsForGender[0];
  }, [listsForGender, selectedListId]);

  const previousList = useMemo(() => {
    if (!activeList?._id || !listsForGender.length) return null;
    const sortedLists = [...listsForGender].sort((a, b) => listSortDate(a) - listSortDate(b));
    const activeIndex = sortedLists.findIndex((list) => list._id === activeList._id);
    if (activeIndex <= 0) return null;
    return sortedLists[activeIndex - 1];
  }, [activeList, listsForGender]);

  const activeSnapshotLists = useMemo(() => {
    if (!activeList) return { men: null, women: null };

    return {
      men:
        gender === 'men'
          ? activeList
          : findMatchingSnapshotList(publishedLists.men, activeList),
      women:
        gender === 'women'
          ? activeList
          : findMatchingSnapshotList(publishedLists.women, activeList),
    };
  }, [activeList, gender, publishedLists.men, publishedLists.women]);

  const fetchCurrentLists = useCallback(async () => {
    setListsLoading(true);
    setListsError('');
    try {
      const res = await publicApi.get('/api/public/rankings');
      setPublishedLists({
        men: res.data.menLists || (res.data.men ? [res.data.men] : []),
        women: res.data.womenLists || (res.data.women ? [res.data.women] : []),
      });
    } catch (err) {
      setListsError(err.response?.data?.message || 'Failed to load rankings');
      setPublishedLists({ men: [], women: [] });
    } finally {
      setListsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentLists();
  }, [fetchCurrentLists]);

  const genderInitialized = useRef(false);
  useEffect(() => {
    if (listsLoading || genderInitialized.current) return;
    genderInitialized.current = true;
    if (!publishedLists.men.length && publishedLists.women.length) {
      setGender('women');
    }
  }, [listsLoading, publishedLists.men.length, publishedLists.women.length]);

  useEffect(() => {
    const lists = publishedLists[gender] || [];
    if (!lists.length) {
      setSelectedListId(null);
      return;
    }
    setSelectedListId((prev) => {
      if (prev && lists.some((l) => l._id === prev)) return prev;
      return lists[0]._id;
    });
  }, [gender, publishedLists]);

  const fetchEntries = useCallback(async () => {
    if (!activeList?._id) {
      setAllEntriesByGender({ men: [], women: [] });
      return;
    }

    setEntriesLoading(true);
    setEntriesError('');
    try {
      const activeSnapshot =
        gender === 'men' ? activeSnapshotLists.men : activeSnapshotLists.women;
      const activeEntries = activeSnapshot?._id
        ? await getRankingEntries(activeSnapshot._id)
        : [];

      setAllEntriesByGender((prev) => ({ ...prev, [gender]: activeEntries }));

      const otherGender = gender === 'men' ? 'women' : 'men';
      const otherSnapshot =
        otherGender === 'men' ? activeSnapshotLists.men : activeSnapshotLists.women;
      if (otherSnapshot?._id) {
        getRankingEntries(otherSnapshot._id)
          .then((otherEntries) => {
            setAllEntriesByGender((prev) => ({ ...prev, [otherGender]: otherEntries }));
          })
          .catch(() => {});
      }
    } catch (err) {
      setEntriesError(err.response?.data?.message || 'Failed to load athletes');
      setAllEntriesByGender((prev) => ({ ...prev, [gender]: [] }));
    } finally {
      setEntriesLoading(false);
    }
  }, [activeList?._id, activeSnapshotLists.men, activeSnapshotLists.women, gender]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    let cancelled = false;

    if (!previousList?._id) {
      setPreviousEntriesByGender((prev) => ({ ...prev, [gender]: [] }));
      return () => {
        cancelled = true;
      };
    }

    getRankingEntries(previousList._id).then((entries) => {
      if (!cancelled) {
        setPreviousEntriesByGender((prev) => ({ ...prev, [gender]: entries }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [previousList?._id, gender]);

  useEffect(() => {
    let cancelled = false;

    if (!activeList?._id) {
      setReplacementGroups([]);
      setReplacementReportDate(null);
      setReplacementDisplayMode('none');
      return () => {
        cancelled = true;
      };
    }

    getRankingReplacements(activeList._id).then(({ replacements, reportDate, displayMode }) => {
      if (!cancelled) {
        setReplacementGroups(replacements);
        setReplacementReportDate(reportDate);
        setReplacementDisplayMode(displayMode || 'none');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [activeList?._id]);

  const fetchRankHistory = useCallback(async () => {
    if (!activeList?._id || !listsForGender.length) {
      setRankHistoryByGender((prev) => ({ ...prev, [gender]: {} }));
      return;
    }

    const sortedLists = [...listsForGender].sort((a, b) => listSortDate(a) - listSortDate(b));
    const activeIndex = sortedLists.findIndex((list) => list._id === activeList._id);
    const endIndex = activeIndex === -1 ? sortedLists.length - 1 : activeIndex;
    const listsThroughActive = sortedLists.slice(0, endIndex + 1);
    const historyStartIndex =
      listsThroughActive.length > FULL_HISTORY_LIMIT
        ? Math.max(0, listsThroughActive.length - RECENT_HISTORY_LIMIT)
        : 0;
    const historyLists = listsThroughActive.slice(historyStartIndex);

    try {
      const entryLists = await Promise.all(historyLists.map((list) => getRankingEntries(list._id)));

      const history = {};
      entryLists.forEach((listEntries, idx) => {
        const list = historyLists[idx];
        const label = `W${historyStartIndex + idx + 1}`;
        const date = list.snapshotDate || list.updatedAt || list.createdAt;
        listEntries.forEach((entry) => {
          const key = entryKey(entry);
          if (!history[key]) history[key] = [];
          history[key].push({
            label,
            date,
            olympicRank: entry.olympicRank,
            entry,
          });
        });
      });

      setRankHistoryByGender((prev) => ({ ...prev, [gender]: history }));
    } catch {
      setRankHistoryByGender((prev) => ({ ...prev, [gender]: {} }));
    }
  }, [activeList?._id, gender, listsForGender]);

  useEffect(() => {
    let cancelled = false;

    const needsFullHistory =
      !isMobileView || activeMobileView === 'stats' || Boolean(selectedPlayer);

    if (!needsFullHistory || !activeList?._id || !listsForGender.length) {
      if (!needsFullHistory) {
        setRankHistoryByGender((prev) => ({ ...prev, [gender]: {} }));
      }
      return () => {
        cancelled = true;
      };
    }

    const run = () => {
      if (!cancelled) fetchRankHistory();
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(run, { timeout: 2000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timeoutId = window.setTimeout(run, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    fetchRankHistory,
    activeList?._id,
    listsForGender.length,
    gender,
    isMobileView,
    activeMobileView,
    selectedPlayer,
  ]);

  useEffect(() => {
    const searchParam = searchParams.get('search') || '';
    const genderParam = searchParams.get('gender');

    if (genderParam === 'men' || genderParam === 'women') {
      setGender(genderParam);
    }

    if (searchParam) {
      setSearch(searchParam);
      setAppliedSearch(searchParam);
      setPage(1);
    }
  }, [searchParams]);

  const activeRawEntries = useMemo(
    () => allEntriesByGender[gender] || [],
    [allEntriesByGender, gender]
  );
  const previousEntries = useMemo(
    () => previousEntriesByGender[gender] || [],
    [previousEntriesByGender, gender]
  );
  const activeRankHistory = useMemo(
    () => rankHistoryByGender[gender] || {},
    [rankHistoryByGender, gender]
  );
  const tableRankHistory = useMemo(
    () => buildTableRankHistory(activeRawEntries, previousEntries),
    [activeRawEntries, previousEntries]
  );
  const replacementByEntryId = useMemo(() => {
    const map = new Map();
    replacementGroups.forEach((group) => {
      map.set(String(group.entryId), group);
    });
    return map;
  }, [replacementGroups]);

  const movementAnalytics = useMemo(
    () => buildMovementAnalytics(activeRankHistory, activeRawEntries),
    [activeRankHistory, activeRawEntries]
  );
  const { rankMovers } = movementAnalytics;

  const movementByKey = useMemo(() => {
    const map = new Map();
    rankMovers.forEach((mover) => map.set(mover.key, mover));
    return map;
  }, [rankMovers]);

  const cutLineWatch = useMemo(
    () => buildCutLineWatch(activeRawEntries, movementByKey, activeRankHistory),
    [activeRawEntries, movementByKey, activeRankHistory]
  );

  const lineCrossers = useMemo(
    () => buildLineCrossersFromSnapshots(previousEntries, activeRawEntries),
    [previousEntries, activeRawEntries]
  );

  const biggestMovers = useMemo(
    () =>
      [...rankMovers]
        .filter((mover) => mover.absDelta > 0)
        .sort(sortByMovement),
    [rankMovers]
  );

  const reallocationReserve = useMemo(
    () => buildReallocationReserve(activeRawEntries, movementByKey, activeRankHistory),
    [activeRawEntries, movementByKey, activeRankHistory]
  );

  const filteredEntries = useMemo(() => {
    const rawTerm = appliedSearch.trim();
    const term = rawTerm.toLowerCase();
    const mappedNoc = getNocFromMapName(rawTerm).toLowerCase();

    return activeRawEntries.filter((entry) => {
      if (continent && getContinent(entry.country) !== continent) return false;
      if (!term) return true;

      const fullName = `${entry.firstName || ''} ${entry.lastName || ''}`.toLowerCase();
      const country = String(entry.country || '').toLowerCase();
      const countryName = getCountryName(entry.country).toLowerCase();
      return (
        fullName.includes(term) ||
        country.includes(term) ||
        countryName.includes(term) ||
        (mappedNoc && country === mappedNoc)
      );
    });
  }, [activeRawEntries, appliedSearch, continent]);

  const filteredEntryKeys = useMemo(
    () => new Set(filteredEntries.map((entry) => entryKey(entry))),
    [filteredEntries]
  );
  const mobileCutLineWatch = useMemo(
    () => cutLineWatch.filter((row) => filteredEntryKeys.has(row.key)),
    [cutLineWatch, filteredEntryKeys]
  );
  const mobileLineCrossers = useMemo(
    () =>
      lineCrossers.filter((mover) =>
        matchesLineCrosserFilters(mover, { term: appliedSearch.trim(), continent })
      ),
    [lineCrossers, appliedSearch, continent]
  );
  const mobileBiggestMovers = useMemo(
    () => biggestMovers.filter((mover) => filteredEntryKeys.has(mover.key)),
    [biggestMovers, filteredEntryKeys]
  );
  const mobileReallocationReserve = useMemo(
    () => reallocationReserve.filter((row) => filteredEntryKeys.has(row.key)),
    [reallocationReserve, filteredEntryKeys]
  );
  const total = filteredEntries.length;
  const entries = useMemo(
    () => filteredEntries.slice((page - 1) * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE),
    [filteredEntries, page]
  );
  const visibleEntries = entries;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE)),
    [total]
  );

  const pageNumbers = useMemo(
    () => buildPageNumbers(page, totalPages),
    [page, totalPages]
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const toggleMobileSection = (sectionId) => {
    setExpandedMobileSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  const handleGenderChange = (g) => {
    setGender(g);
    setPage(1);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('gender', g);
        const searchValue = appliedSearch.trim() || current.get('search') || '';
        if (searchValue) {
          next.set('search', searchValue);
        } else {
          next.delete('search');
        }
        return next;
      },
      { replace: true }
    );
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!activeList) return;
    setAppliedSearch(search);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    if (!value.trim() && appliedSearch) {
      setAppliedSearch('');
      setPage(1);
    }
  };

  const handleWeekChange = (listId) => {
    setSelectedListId(listId);
    setPage(1);
  };

  const handleContinentChange = (value) => {
    setContinent(value);
    setPage(1);
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * DEFAULT_PAGE_SIZE + 1;
  const showingTo = Math.min(page * DEFAULT_PAGE_SIZE, total);
  const lastUpdated = activeList
    ? formatDate(activeList.snapshotDate || activeList.updatedAt || activeList.createdAt)
    : '—';
  const selectedWeekLabel =
    weekOptions.find((option) => option.value === activeList?._id)?.label ||
    (activeList ? formatDate(activeList.snapshotDate || activeList.updatedAt || activeList.createdAt) : 'Latest week');
  const genderLabel = gender === 'women' ? "Women's" : "Men's";

  const genderTabs = (
    <nav
      className="rankings-category-tabs rankings-gender-row"
      role="tablist"
      aria-label="Ranking category"
    >
      {GENDER_OPTIONS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          role="tab"
          id={`rankings-tab-${opt.id}`}
          aria-selected={gender === opt.id}
          aria-controls="rankings-tabpanel"
          className={`rankings-category-tab ${gender === opt.id ? 'active' : ''}`}
          onClick={() => handleGenderChange(opt.id)}
        >
          {opt.tabLabel}
        </button>
      ))}
    </nav>
  );

  const mobileViewTabs = (
    <nav className="rankings-mobile-view-tabs" aria-label="Mobile rankings view">
      {[
        { id: 'rankings', label: 'Rankings' },
        { id: 'stats', label: 'Stats' },
      ].map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={`rankings-mobile-view-tab ${activeMobileView === tab.id ? 'is-active' : ''}`}
          aria-pressed={activeMobileView === tab.id}
          onClick={() => setActiveMobileView(tab.id)}
        >
          <i className={`bi ${tab.id === 'rankings' ? 'bi-list-ol' : 'bi-bar-chart-line'}`} aria-hidden />
          <span>{tab.label}</span>
        </button>
      ))}
    </nav>
  );

  const filtersBar = (
    <div className={`rankings-filters ${showMobileFilters ? 'is-expanded' : ''}`}>
      <div className="rankings-filters-inner">
        <form className="rankings-filters-form" onSubmit={handleSearchSubmit}>
          <div className="rankings-filters-row">
            <label className="filter-field filter-field-search">
              <span className="filter-field-label">Search</span>
              <span className="filter-underline-wrap filter-search-wrap">
                <i className="bi bi-search" aria-hidden />
                <input
                  type="search"
                  className="filter-underline-input"
                  placeholder="Search athlete / country…"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  disabled={!activeList}
                />
              </span>
            </label>

            <button
              type="button"
              className="rankings-mobile-filter-toggle"
              aria-expanded={showMobileFilters}
              onClick={() => setShowMobileFilters((open) => !open)}
            >
              <i className="bi bi-sliders" aria-hidden />
              Filters
            </button>

            <div className="filter-field filter-field-continent filter-field-advanced">
              <FilterSelect
                label="Continent"
                value={continent}
                onChange={handleContinentChange}
                options={CONTINENT_FILTER_OPTIONS}
                disabled={!activeList}
              />
            </div>

            <div className="filter-field filter-field-week filter-field-advanced">
              <FilterSelect
                label="Week"
                value={activeList?._id || ''}
                onChange={handleWeekChange}
                options={weekOptions}
                disabled={!weekOptions.length}
                placeholder="No weeks yet"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  const mobileWatchSections = [
    {
      id: 'crossers',
      title: 'Moved In / Moved Out',
      description: 'Athletes who entered or exited the top 60 OGR since the previous published ranking',
      countLabel: `${mobileLineCrossers.length} changes`,
      empty: 'No one crossed the qualification line between the last two lists.',
      rows: mobileLineCrossers.map((mover) => ({
        key: mover.key,
        entry: mover.entry,
        meta: formatLineCrosserMeta(mover),
        badge: mover.crossingType === 'in' ? 'IN' : 'OUT',
        badgeTone: mover.crossingType === 'in' ? 'green' : 'red',
        onClick: () => setSelectedPlayer(mover.entry),
      })),
    },
    {
      id: 'cutline',
      title: 'Current Qualifier Watch',
      description: SHOW_EXTENDED_RANKS
        ? 'Ranks 55-65 around the Olympic qualification line'
        : 'Athletes currently ranked 55–60, closest to the Olympic qualification line',
      countLabel: `${mobileCutLineWatch.length} athletes`,
      empty: 'No qualification-line athletes in this window.',
      rows: mobileCutLineWatch.map((row) => ({
        key: row.key,
        entry: row.entry,
        meta: `Rank ${row.endRank} | ${row.lineStatus === 'outside' ? 'First outside' : 'Inside line'}`,
        onClick: () => setSelectedPlayer(row.entry),
      })),
    },
    {
      id: 'reserve',
      title: 'Just Outside the Line',
      description: 'Players immediately outside the Olympic qualification line',
      countLabel: `${mobileReallocationReserve.length} athletes`,
      empty: 'No athletes just outside the line in this window.',
      rows: mobileReallocationReserve.map((row) => ({
        key: row.key,
        entry: row.entry,
        meta: `${row.endRank - QUALIFICATION_CUTOFF} outside | Rank ${row.endRank}`,
        onClick: () => setSelectedPlayer(row.entry),
      })),
    },
    {
      id: 'movers',
      title: 'Biggest Movers',
      description: 'Largest Olympic-rank moves since the previous list',
      countLabel: `${mobileBiggestMovers.length} movers`,
      empty: 'No major rank changes since the previous list.',
      rows: mobileBiggestMovers.map((mover) => ({
        key: mover.key,
        entry: mover.entry,
        meta: `Rank ${mover.endRank} | ${mover.delta > 0 ? '+' : ''}${mover.delta}`,
        badge: mover.delta > 0 ? `+${mover.delta}` : `${mover.delta}`,
        badgeTone: mover.delta > 0 ? 'green' : 'red',
        onClick: () => setSelectedPlayer(mover.entry),
      })),
    },
  ];

  const mobileHighlights = activeList ? (
    <section className="rankings-mobile-highlights" aria-label="Qualification Watch">
      <Link to="/rankings/insights" className="mobile-insights-promo">
        <span className="mobile-insights-promo-icon" aria-hidden>
          <i className="bi bi-bar-chart-line" />
        </span>
        <span className="mobile-insights-promo-copy">
          <strong>Full qualification breakdown</strong>
          <small>Continent charts, world map, and country details</small>
        </span>
        <i className="bi bi-arrow-right-short" aria-hidden />
      </Link>
      <div className="mobile-highlights-heading">
        <span>Qualification Watch</span>
        <small>Key stories before the full rankings</small>
      </div>
      <div className="mobile-watch-stack">
        {mobileWatchSections.map((section) => {
          const isExpanded = Boolean(expandedMobileSections[section.id]);
          const visibleRows = isExpanded
            ? section.rows
            : section.rows.slice(0, MOBILE_WATCH_PREVIEW_COUNT);
          const hasHiddenRows = section.rows.length > MOBILE_WATCH_PREVIEW_COUNT;

          return (
            <section key={section.id} className="mobile-watch-card">
              <header className="mobile-watch-card-header">
                <span>
                  <strong>{section.title}</strong>
                  <small>{section.description}</small>
                </span>
                <em>{section.countLabel}</em>
              </header>

              <div className="mobile-watch-list">
                {visibleRows.length ? (
                  visibleRows.map((row) => (
                    <button
                      key={row.key}
                      type="button"
                      className="mobile-watch-row"
                      onClick={row.onClick}
                    >
                      {row.entry ? (
                        <PlayerCell
                          firstName={row.entry.firstName}
                          lastName={row.entry.lastName}
                          country={row.entry.country}
                          headshotUrl={row.entry.headshotUrl}
                        />
                      ) : (
                        <span className="mobile-watch-country">
                          <CountryFlag code={row.country} size={18} />
                          <strong>{row.title}</strong>
                        </span>
                      )}
                      <span className="mobile-watch-meta">{row.meta}</span>
                      {row.badge && (
                        <span className={`mobile-watch-badge mobile-watch-badge--${row.badgeTone}`}>
                          {row.badge}
                        </span>
                      )}
                      <i className="bi bi-chevron-right" aria-hidden />
                    </button>
                  ))
                ) : (
                  <p className="mobile-watch-empty">{section.empty}</p>
                )}
              </div>

              {hasHiddenRows && (
                <button
                  type="button"
                  className="mobile-watch-toggle"
                  onClick={() => toggleMobileSection(section.id)}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? 'Show less' : `View all ${section.rows.length}`}
                </button>
              )}
            </section>
          );
        })}
      </div>
    </section>
  ) : null;

  const mobileRankingsHeader = activeList ? (
    <div className="rankings-mobile-list-header">
      <div>
        <p>Rankings</p>
        <h2>Olympic Golf Rankings Tracker</h2>
        <span>{genderLabel} | {selectedWeekLabel}</span>
      </div>
      <strong>
        {visibleEntries.length} of {total}
      </strong>
      <small>Tap an athlete to view progression</small>
    </div>
  ) : null;

  const showRankingsContent = !isMobileView || activeMobileView === 'rankings';
  const showStatsContent = isMobileView && activeMobileView === 'stats';

  return (
    <div className="rankings-page">
      <section
        className="rankings-hero"
        style={{ backgroundImage: HERO_BACKGROUND }}
      >
        <div className="rankings-hero-overlay" />
        <div className="rankings-hero-inner">
          <h1 className="rankings-hero-title">Olympic Golf Rankings Tracker</h1>
          <p className="rankings-hero-subtitle">Road to LA 2028</p>
          {activeList && !listsLoading && (
            <div className="rankings-hero-actions">
              <div className="rankings-hero-stats">
                <div className="hero-stat">
                  <span className="hero-stat-icon-wrap">
                    <i className="bi bi-calendar3" aria-hidden />
                  </span>
                  <span className="hero-stat-text">
                    <span className="hero-stat-label">As of</span>
                    <strong>{lastUpdated}</strong>
                  </span>
                </div>
              </div>
              <Link to="/rankings/insights" className="hero-insights-cta">
                <i className="bi bi-bar-chart-line" aria-hidden />
                <span>Explore qualification insights</span>
                <i className="bi bi-arrow-right-short hero-insights-cta-arrow" aria-hidden />
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="rankings-body">
        {listsLoading ? (
          <div className="rankings-body-inner">
            <div className="rankings-loading">
              <div className="spinner-border text-success" role="status" />
            </div>
          </div>
        ) : listsError ? (
          <div className="rankings-body-inner rankings-body-inner--padded">
            <div className="alert alert-danger">{listsError}</div>
          </div>
        ) : (
          <>
            <div className="rankings-desktop-filters">{filtersBar}</div>
            <div className="rankings-body-inner">
              <div className="rankings-content-grid">
                {!isMobileView && (
                  <BubbleWatchRail
                    cutLineWatch={cutLineWatch}
                    gender={gender}
                    lineCrossers={lineCrossers}
                    list={activeList}
                    showExtendedRanks={SHOW_EXTENDED_RANKS}
                    onPlayerClick={setSelectedPlayer}
                  />
                )}

                <div className="rankings-main-panel">
              <section
                id="rankings-tabpanel"
                className="rankings-list-area"
                role="tabpanel"
                aria-labelledby={`rankings-tab-${gender}`}
              >
                <div className="rankings-tabs-wrap">{genderTabs}</div>
                {mobileViewTabs}
                <div className="rankings-mobile-filters-wrap">{filtersBar}</div>
                {showStatsContent && mobileHighlights}

                {!activeList ? (
                  <div className="rankings-empty">
                    <i className="bi bi-trophy" />
                    <h2>No published {gender === 'men' ? "men's" : "women's"} rankings yet</h2>
                    <p>
                      {gender === 'men' ? "Men's" : "Women's"} rankings will appear here once the IGF
                      publishes an updated list. Select the other tab to check both categories.
                    </p>
                  </div>
                ) : (
                  <>
                    {entriesError && (
                      <div className="alert alert-danger rankings-list-alert">{entriesError}</div>
                    )}

                    {showRankingsContent && (
                      <div className="rankings-table-panel">
                        {mobileRankingsHeader}
                        <RankingTable
                          entries={visibleEntries}
                          loading={entriesLoading}
                          onRowClick={setSelectedPlayer}
                          rankHistory={tableRankHistory}
                          gender={gender}
                        />

                        {total > 0 && (
                          <div className="rankings-table-footer">
                            <p className="rankings-showing">
                              Showing {showingFrom} to {showingTo} of {total} athletes
                            </p>
                            {totalPages > 1 && (
                              <nav className="rankings-pager" aria-label="Rankings pagination">
                                <button
                                  type="button"
                                  className="pager-btn"
                                  disabled={page <= 1 || entriesLoading}
                                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                                  aria-label="Previous page"
                                >
                                  <i className="bi bi-chevron-left" />
                                </button>
                                {pageNumbers.map((n, idx) =>
                                  n === 'ellipsis' ? (
                                    <span key={`e-${idx}`} className="pager-ellipsis">
                                      …
                                    </span>
                                  ) : (
                                    <button
                                      key={n}
                                      type="button"
                                      className={`pager-num ${page === n ? 'active' : ''}`}
                                      disabled={entriesLoading}
                                      onClick={() => setPage(n)}
                                      aria-current={page === n ? 'page' : undefined}
                                    >
                                      {n}
                                    </button>
                                  )
                                )}
                                <button
                                  type="button"
                                  className="pager-btn"
                                  disabled={page >= totalPages || entriesLoading}
                                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                  aria-label="Next page"
                                >
                                  <i className="bi bi-chevron-right" />
                                </button>
                              </nav>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </section>
                </div>

                {!isMobileView && (
                  <AthleteHubRail
                    biggestMovers={biggestMovers}
                    outsideLine={reallocationReserve}
                    list={activeList}
                    onPlayerClick={setSelectedPlayer}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {selectedPlayer && (
        <Suspense fallback={null}>
          <PlayerProgressionModal
            player={selectedPlayer}
            history={activeRankHistory[entryKey(selectedPlayer)] || tableRankHistory[entryKey(selectedPlayer)] || []}
            replacementGroup={replacementByEntryId.get(String(selectedPlayer._id))}
            replacementReportDate={replacementReportDate}
            replacementDisplayMode={replacementDisplayMode}
            gender={gender}
            onClose={() => setSelectedPlayer(null)}
          />
        </Suspense>
      )}

    </div>
  );
};

export default RankingsPage;
